/* PauseAction.js */

"use strict";
const PauseQuestionComponent = require("./PauseQuestionComponent");
const {managerFor, refusalMessage} = require("../../../../lib/managedSchedules");

const SCHEDULE_NAME_MAX_LENGTH = 60;
const SCHEDULE_NAME_LIST_LENGTH = 10;

const PAUSE_MESSAGE =
    "Are you sure you want to pause the selected {NUM} schedule(s) from being run?";
const RESUME_MESSAGE = "Are you sure you want to resume running the selected {NUM} schedule(s)?";
const PAUSE_RESUME_ALL_MESSAGE =
    "Are you sure you want to pause/resume the selected {NUM} schedules?";
const NOT_ALLOWED_MESSAGE =
    "Sorry! You cannot pause an action that is currently running or already finished. You can only pause actions scheduled for the future.";

const PAUSE_TITLE = "Pause";
const RESUME_TITLE = "Resume";

const PAUSE_RESUME_TITLE = "Pause/Resume";

const CONFIRM_LABEL = "Confirm";
const CANCEL_LABEL = "Cancel";
const PAUSE_ALL_LABEL = "Pause All";
const RESUME_ALL_LABEL = "Resume All";

const ALLOWED_STATUSES = ["ACTIVE", "PAUSED"];

const getScheduleName = (entity) => {
    let name = entity.displayName;
    if (name.length > SCHEDULE_NAME_MAX_LENGTH) {
        name = name.substr(0, SCHEDULE_NAME_MAX_LENGTH) + "…";
    }
    return name;
};
const pluralizeMessage = (msg, names) => {
    let message = msg.replace("schedule(s)", names.length > 1 ? "schedules" : "schedule");
    if (names.length > SCHEDULE_NAME_LIST_LENGTH) {
        message = message.replace("{NUM}", names.length);
    } else {
        message = message.replace("{NUM}", "");
        message += "\n\n" + names.join("\n");
    }
    return message;
};
const getMessage = (isPaused) => (isPaused ? RESUME_MESSAGE : PAUSE_MESSAGE);
const getTitle = (isPaused) => (isPaused ? RESUME_TITLE : PAUSE_TITLE);
const getBtnLabel = (isPaused, isMultiple) => {
    let label = CONFIRM_LABEL;
    if (isMultiple) {
        label = isPaused ? RESUME_ALL_LABEL : PAUSE_ALL_LABEL;
    }
    return label;
};

class PauseAction {
    constructor(gameEditor, activeView, modalUserFeedback, logger) {
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._activeView = activeView;
        this._modalUserFeedback = modalUserFeedback;
        this._selectedTriggerTypeIds = [];

        if (this._activeView) {
            if (this._activeView.pagingFeed) {
                this._selectedTriggerTypeIds =
                    this._activeView.getSelectedTriggerTypeIds().selectedRows || [];
            }
        }

        let sort = (this._activeView &&
            this._activeView._pagingFeed &&
            this._activeView._pagingFeed.getSort()) || {name: 1};
        this._selectedTriggerTypes = {
            sort: sort,
            ids: this._selectedTriggerTypeIds,
        };

        this.run = this.run.bind(this);
    }

    run() {
        return this._activeView.pagingFeed
            .waitForRangeIdsUpdated()
            .then(() => {
                return this._activeView.pagingFeed.getTriggerTypeEntitiesById(
                    this._selectedTriggerTypes.ids
                );
            })
            .then((entities) => {
                const isMultiple = entities.length > 1;
                const {allArePaused, allAreRunning, isMixed, notAllowed} =
                    this._getStatus(entities);

                // Refused for the whole selection rather than silently filtered, so the user is
                // never told a partial action succeeded.
                const managed = entities.map((entity) => managerFor(entity)).find((m) => m);
                if (managed) {
                    return this._modalUserFeedback.displayError({
                        errorMsg: refusalMessage(managed, allArePaused ? "resumed" : "paused"),
                        errorCode: "",
                        title: PAUSE_RESUME_TITLE,
                    });
                }

                if (notAllowed) {
                    return this._modalUserFeedback.displayError({
                        error: {
                            errorTitle: "Error",
                            errorMsg: NOT_ALLOWED_MESSAGE,
                            errorCode: "",
                        },
                    });
                } else if (allArePaused || allAreRunning) {
                    return this._gameEditor.runQuickConfirmationActionWorkflow(
                        {
                            title: getTitle(allArePaused, isMultiple),
                            message: pluralizeMessage(
                                getMessage(allArePaused, isMultiple),
                                entities.map(getScheduleName)
                            ),
                            confirmDetails: {
                                text: getBtnLabel(allArePaused, isMultiple),
                                type: "additive",
                            },
                            rejectDetails: {
                                text: CANCEL_LABEL,
                                type: "text",
                            },
                        },
                        () => {
                            return allArePaused
                                ? this.resumeAll(entities)
                                : this.pauseAll(entities);
                        }
                    );
                } else if (isMixed) {
                    return this._modalUserFeedback
                        .displayCustom(
                            new PauseQuestionComponent(this._logger, {
                                title: PAUSE_RESUME_TITLE,
                                message: pluralizeMessage(
                                    PAUSE_RESUME_ALL_MESSAGE,
                                    entities.map(getScheduleName)
                                ),
                                buttons: [
                                    {label: CANCEL_LABEL, type: "text"},
                                    {
                                        label: PAUSE_ALL_LABEL,
                                        action: () => this.pauseAll(entities),
                                        type: "additive",
                                    },
                                    {
                                        label: RESUME_ALL_LABEL,
                                        action: () => this.resumeAll(entities),
                                        type: "additive",
                                    },
                                ],
                            })
                        )
                        .then((action) => {
                            return this._modalUserFeedback
                                .displayLoading()
                                .then(() => action && action());
                        })
                        .catch((err) => {
                            if (!err.userRejected) {
                                this._logger.warn(err, "GameCenter.PauseResumeAction");
                            }
                        })
                        .then(() => this._modalUserFeedback.hideDisplayed());
                } else {
                    return Promise.resolve();
                }
            })
            .catch((error) => {
                this._logger.warn({error}, "PauseAction.run() - Error");
            });
    }

    /**
     * Resume all entities
     * @param entities {Array<TriggerTypeEntity>}
     * @returns {Promise}
     */
    resumeAll(entities) {
        return Promise.all(entities.map((entity) => entity.resumeTriggers()));
    }

    /**
     * Pause all entities
     * @param entities {Array<TriggerTypeEntity>}
     * @returns {Promise}
     */
    pauseAll(entities) {
        return Promise.all(entities.map((entity) => entity.pauseTriggers()));
    }

    /**
     * @typedef {Object} SelectedRangeStatus
     * @property {boolean} allArePaused - means all entities in the range are paused
     * @property {boolean} allAreRunning - means all entities in the range are running
     * @property {boolean} isMixed - means there are different statuses in the range
     * @property {boolean} notAllowed - there is at least one entity can neither be paused nor resumed
     *
     * Find out status of the selected range
     * @param entities {Array<TriggerTypeEntity>}
     * @returns {SelectedRangeStatus} - object with state values
     * @private
     */
    _getStatus(entities = []) {
        if (!entities || !entities.length) {
            return {
                allArePaused: false,
                allAreRunning: false,
                isMixed: false,
                notAllowed: false,
            };
        }

        let allArePaused = true;
        let allAreRunning = true;
        let notAllowed = false;
        const isMixed = entities.some(({isPaused, status}, idx, entities) => {
            const isMixed = isPaused !== entities[idx - !!idx].isPaused;
            allArePaused = allArePaused && isPaused;
            allAreRunning = allAreRunning && !isPaused;
            notAllowed = notAllowed || !ALLOWED_STATUSES.includes(status);
            return isMixed || notAllowed;
        });

        return {
            allArePaused: !isMixed && allArePaused,
            allAreRunning: !isMixed && allAreRunning,
            notAllowed,
            isMixed,
        };
    }
}

module.exports = PauseAction;

PauseAction.$inject = [
    "gameEditor.gameEditor",
    "gameEditor.activeView",
    "$teve.ui.userFeedback",
    "$teve.logger",
];

PauseAction.$displayDetails = {
    type: "Action",
    name: "PauseAction",
    icon: "worktrigger.pause",
    title: "Pause/Resume",
    shown: true,
    order: 2,
};
